// Multi-environment configuration constants for the SmartWater dashboard website.

export type Stage = 'beta' | 'gamma' | 'prod';

export interface CognitoConfig {
  userPoolId: string;
  clientId: string;
  domain: string;
  redirectUri: string;
}

/**
 * Determines the current stage from the browser's hostname, following the exact
 * SmartWaterCDK domain convention (SmartWaterCDK/lib/common/constants.ts
 * getWebsiteDomain): bare `smartwater.pv.amazon.dev` for prod,
 * `{stage}.smartwater.pv.amazon.dev` otherwise. `localhost` maps to beta since
 * local dev only ever runs against the beta Cognito pool.
 *
 * @returns The detected stage.
 * @throws Error if the hostname doesn't match any known SmartWater domain.
 */
export const getCurrentStage = (): Stage => {
  const hostname = window.location.hostname;

  if (hostname.includes('beta.smartwater.pv.amazon.dev') || hostname.includes('localhost')) {
    return 'beta';
  } else if (hostname.includes('gamma.smartwater.pv.amazon.dev')) {
    return 'gamma';
  } else if (hostname.includes('smartwater.pv.amazon.dev')) {
    return 'prod';
  }

  throw new Error(
    `Cannot determine stage from hostname: ${hostname}. Expected beta.smartwater.pv.amazon.dev, ` +
      'gamma.smartwater.pv.amazon.dev, or smartwater.pv.amazon.dev',
  );
};

// Per-stage Cognito settings. Must exactly match SmartWaterCDK/lib/common/constants.ts's
// getUserPoolId/getUserPoolClientId/getCognitoDomain -- these are the CDK-managed source
// of truth; this is a hand-kept mirror on the frontend side (the two packages don't share
// a build-time dependency, so there's no automated sync -- see the CDK-side two-phase
// bootstrap comment on getUserPoolId for why these are hardcoded there too).
const cognitoConfigs: Record<Stage, Omit<CognitoConfig, 'redirectUri'>> = {
  beta: {
    userPoolId: 'us-east-1_OTmO8uR53',
    clientId: '3iqdo2r6uetrg2n4o2vhini6da',
    domain: 'smartwater-dashboard-auth-beta.auth.us-east-1.amazoncognito.com',
  },
  gamma: {
    userPoolId: 'us-east-1_QQT7VVs35',
    clientId: '5t3098qvvl6gbulnthb1d8esd4',
    domain: 'smartwater-dashboard-auth-gamma.auth.us-east-1.amazoncognito.com',
  },
  prod: {
    userPoolId: 'us-east-1_sVoYVV4TM',
    clientId: '2u6hi685460c62fkb3boi2i0n',
    domain: 'smartwater-dashboard-auth-prod.auth.us-east-1.amazoncognito.com',
  },
};

/**
 * Returns the Cognito configuration for the current stage. `redirectUri` is always
 * the bare origin -- matches the edge Lambda's `buildLoginRedirect` (redirectUri has
 * no dedicated path) and the Cognito App Client's registered callback URL.
 */
export const getCognitoConfig = (): CognitoConfig => {
  const stage = getCurrentStage();
  return { ...cognitoConfigs[stage], redirectUri: window.location.origin };
};

/**
 * Per-stage backend endpoints for the real-time dashboard: the read API
 * (league discovery, archive read, past-runs list) and the AppSync Events
 * API's HTTP and real-time domains -- these are genuinely distinct
 * subdomains (`appsync-api` vs `appsync-realtime-api`), not derivable from
 * one another, so both are carried separately (confirmed against AWS's own
 * AppSync Events WebSocket protocol reference). Hand-kept mirror of
 * SmartWaterCDK's PipelineEventsStack outputs (readApiUrl,
 * eventApiHttpEndpoint, eventApiRealtimeDns) -- same no-build-time-dependency
 * pattern as cognitoConfigs above. Populated once each stage's stack has
 * been deployed and the real URLs are known.
 */
export interface DashboardConfig {
  readApiUrl: string;
  eventApiHttpDomain: string;
  eventApiRealtimeDomain: string;
}

const dashboardConfigs: Record<Stage, DashboardConfig> = {
  beta: {
    readApiUrl: 'https://lx5bpdip7c.execute-api.us-east-1.amazonaws.com/beta',
    eventApiHttpDomain: 'qgvd6fcu75hififh6lgcpheyuq.appsync-api.us-east-1.amazonaws.com',
    eventApiRealtimeDomain: 'qgvd6fcu75hififh6lgcpheyuq.appsync-realtime-api.us-east-1.amazonaws.com',
  },
  gamma: {
    readApiUrl: 'https://3blvsbdkrh.execute-api.us-east-1.amazonaws.com/gamma',
    eventApiHttpDomain: '3hr3ukpicvddvfrec3vg543aju.appsync-api.us-east-1.amazonaws.com',
    eventApiRealtimeDomain: '3hr3ukpicvddvfrec3vg543aju.appsync-realtime-api.us-east-1.amazonaws.com',
  },
  prod: {
    readApiUrl: 'https://fkalgiehq9.execute-api.us-east-1.amazonaws.com/prod',
    eventApiHttpDomain: 'i5yc2ny3nrb5lmgdqfduvbxele.appsync-api.us-east-1.amazonaws.com',
    eventApiRealtimeDomain: 'i5yc2ny3nrb5lmgdqfduvbxele.appsync-realtime-api.us-east-1.amazonaws.com',
  },
};

/**
 * Returns the dashboard backend config for the current stage.
 *
 * @throws Error if the current stage's config has not yet been filled in --
 *   fails explicitly rather than silently pointing the dashboard at an
 *   empty URL, per coding-standards.md's over-helpfulness guard.
 */
export const getDashboardConfig = (): DashboardConfig => {
  const stage = getCurrentStage();
  const config = dashboardConfigs[stage];
  if (!config.readApiUrl || !config.eventApiHttpDomain || !config.eventApiRealtimeDomain) {
    throw new Error(
      `Dashboard backend config for stage '${stage}' is not yet set (see common/constants.ts's ` +
        "dashboardConfigs) -- fill in readApiUrl/eventApiHttpDomain/eventApiRealtimeDomain from " +
        "PipelineEventsStack's deployed outputs before using the dashboard in this stage.",
    );
  }
  return config;
};
